<template>
    <DsSingleSelect
        v-model="selectedCompany"
        :id="`ds-select-company-${id}`"
        class="col-span-12 md:col-span-6"
        :disabled="disableCompany || isSoleCompanyAndBranch"
        :error="companyError"
        label="Company"
        :options="companyOptions"
        placeholder="Select company"
        required
        @optionSelected="onCompanySelected"
    />
    <DsSingleSelect
        v-model="selectedBranch"
        :id="`ds-select-branch-${id}`"
        class="col-span-12 md:col-span-6"
        :disabled="disableBranch || isSoleCompanyAndBranch || !selectedCompany"
        :error="branchError"
        label="Branch"
        :options="branchOptions"
        placeholder="Select branch"
        required
        @optionSelected="$emit('fieldInput', FileDetailErrorKeys.BRANCH_ID)"
    />
    <!-- @todo [CLENG-1942] remove hidden inputs needed for form submission -->
    <input
        id="select-company"
        class="hidden"
        :data-name="selectedCompany?.name ?? ''"
        name="company_id"
        type="hidden"
        :value="selectedCompany?.id ?? ''"
    />
    <input
        id="select-branch"
        class="hidden"
        :data-name="selectedBranch?.name ?? ''"
        name="branch_id"
        type="hidden"
        :value="selectedBranch?.id ?? ''"
    />
</template>

<script setup lang="ts">
import { FileDetailErrorKeys } from '@/company-portal/add-file/add-file-form-errors.types';
import DsSingleSelect from '@/components/design-system/singleselect/DsSingleSelect.vue';
import type { Branch, Company, DsSingleSelectBranchOption, DsSingleSelectCompanyOption } from '@/types/closinglock.types';
import { computed, useId, watch } from 'vue';

export interface SelectCompanyBranchProps {
    branchError?: string;
    branches: Branch[];
    companies: Company[];
    companyError?: string;
    disableBranch?: boolean;
    disableCompany?: boolean;
    initialBranchId?: Branch['id'];
    initialCompanyId?: Company['id'];
}

const props = defineProps<SelectCompanyBranchProps>();

const emit = defineEmits<{
    fieldInput: [field: FileDetailErrorKeys];
}>();

const id = useId();

// company
const selectedCompany = defineModel<Company>('company');

const companyOptions = computed<DsSingleSelectCompanyOption[]>(() => {
    return props.companies.map(company => ({
        ...company,
        id: company.id,
        value: company.id,
        label: company.name,
    }));
});

const isSoleCompany = computed(() => props.companies.length === 1);

const initialCompany = computed<Company | undefined>(() => {
    const byId = props.companies.find(company => company.id === props.initialCompanyId);
    const soleCompany = isSoleCompany.value ? props.companies[0] : undefined;
    return props.initialCompanyId ? byId : soleCompany;
});

watch(initialCompany, newInitialCompany => {
    selectedCompany.value = newInitialCompany;
    emit('fieldInput', FileDetailErrorKeys.COMPANY_ID);
});

// branch
const selectedBranch = defineModel<Branch>('branch');

const branchesFilteredByCompany = computed<Branch[]>(() => {
    return props.branches.filter(branch => branch.title.id === selectedCompany.value?.id);
});

const branchOptions = computed<DsSingleSelectBranchOption[]>(() => {
    return branchesFilteredByCompany.value.map(branch => ({
        ...branch,
        id: branch.id,
        value: branch.id,
        label: branch.name,
    }));
});

const isSoleBranch = computed(() => branchesFilteredByCompany.value.length === 1);

const initialBranch = computed<Branch | undefined>(() => {
    const byId = branchesFilteredByCompany.value.find(branch => branch.id === props.initialBranchId);
    const soleBranch = isSoleBranch.value ? branchesFilteredByCompany.value[0] : undefined;
    return props.initialBranchId ? byId : soleBranch;
});

watch(initialBranch, newInitialBranch => {
    selectedBranch.value = newInitialBranch;
    emit('fieldInput', FileDetailErrorKeys.BRANCH_ID);
});

const onCompanySelected = () => {
    selectedBranch.value = undefined;
    emit('fieldInput', FileDetailErrorKeys.COMPANY_ID);
    emit('fieldInput', FileDetailErrorKeys.BRANCH_ID);
};

const isSoleCompanyAndBranch = computed(() => {
    return isSoleCompany.value && isSoleBranch.value;
});
</script>
